import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $generateOutgoingPaymentStore, $generateReceiptByIdStore, $messageCentreStore, $paymentByIdStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { AmountUtils, cn, ColorUtils, DateUtils, FormatUtils, PaletteUtils, RoutePaths } from '@shared/utils';
import { DepositPaymentDirectionEnum, DepositPaymentTypeOfTransferEnum } from '@shared/api';
import { ExchangeRate, MessageTemplates, PageContainer, View } from '@widgets';
import { Button, Icon, Spinner, Text } from '@shared/widgets';
import TransactionLogoIcon from '@assets/icons/transactions/transaction-logo.svg';
import GobblerIcon from '@assets/icons/transactions/gobbler.svg';
import DocumentIcon from '@assets/icons/document.svg';

export const TransactionPage = () => {
  const { id } = useParams<{ id: string }>();
  const { navigate } = useRouteHook();
  const [searchParams] = useSearchParams();
  const back = searchParams.get('back');

  const paymentStore = useUnit($paymentByIdStore.store);
  const generateReceiptByIdStore = useUnit($generateReceiptByIdStore.store);
  const generateOutgoingPaymentStore = useUnit($generateOutgoingPaymentStore.store);
  const messageCentreStore = useUnit($messageCentreStore.store);

  const { t } = useTranslation('transaction');

  useEffect(() => {
    if (!id) return;
    $paymentByIdStore.events.getPaymentById({ id });
    return () => $paymentByIdStore.events.clear();
  }, [id]);

  useEffect(() => {
    if (paymentStore.loading) return;
    if (paymentStore.error !== 'Action is forbidden.') return;
    $paymentByIdStore.events.clear();
    navigate(RoutePaths.error.onNavigate(), { replace: true });
  }, [paymentStore.loading, paymentStore.error]);

  const downloadHandler = () =>
    paymentStore.data?.direction === DepositPaymentDirectionEnum.Incoming ||
    paymentStore.data?.typeOfTransfer === DepositPaymentTypeOfTransferEnum.Charge
      ? $generateReceiptByIdStore.events.generateReceipt(id!)
      : $generateOutgoingPaymentStore.events.generateOutgoingPayment(id!);

  const content = useMemo(() => {
    if (!paymentStore.data) return [];
    const isCharge = paymentStore.data.typeOfTransfer === DepositPaymentTypeOfTransferEnum.Charge;
    const isIncomingAllocation =
      paymentStore.data.typeOfTransfer === DepositPaymentTypeOfTransferEnum.Allocation &&
      paymentStore.data.direction === DepositPaymentDirectionEnum.Incoming;
    const isOutgoingAllocation =
      paymentStore.data.typeOfTransfer === DepositPaymentTypeOfTransferEnum.Allocation &&
      paymentStore.data.direction === DepositPaymentDirectionEnum.Outgoing;
    return [
      [
        {
          show: true,
          label: t('details.fields.typeOfTransaction'),
          value: FormatUtils.transactionTypeTransformation(t(`transferRequestType.${paymentStore.data.typeOfTransfer}`, { ns: 'common' })),
        },
        { show: !isCharge && !isIncomingAllocation, label: t('details.fields.senderName'), value: paymentStore.data.senderName },
        { show: !isCharge && !isIncomingAllocation, label: t('details.fields.senderIban'), value: paymentStore.data.senderIban },
        {
          show: !!paymentStore.data.beneficiaryName && !isCharge && !isIncomingAllocation && !isOutgoingAllocation,
          label: t('details.fields.beneficiaryName'),
          value: paymentStore.data.beneficiaryName,
        },
        { show: !isCharge && !isOutgoingAllocation, label: t('details.fields.beneficiaryIban'), value: paymentStore.data.beneficiaryIban },
      ],
      [
        { show: !isCharge && !isIncomingAllocation, label: t('details.fields.senderCurrency'), value: paymentStore.data.currencyIn },
        { show: !isCharge && !isOutgoingAllocation, label: t('details.fields.beneficiaryCurrency'), value: paymentStore.data.currencyOut },
        {
          show: !isCharge && !isIncomingAllocation && !isOutgoingAllocation,
          label: t('details.fields.exchangeRate'),
          value: `${AmountUtils.transformToAmountWithCurrency(1, paymentStore.data.currencyIn)} = ${AmountUtils.transformToAmountWithCurrency(paymentStore.data.exchangeRate, paymentStore.data.currencyOut)}`,
          children: <ExchangeRate />,
        },
        {
          show: !isCharge && !isOutgoingAllocation,
          label: t('details.fields.beneficiaryAmount'),
          value: AmountUtils.transformToAmountWithCurrency(paymentStore.data.beneficiaryAmount, paymentStore.data.currencyOut),
        },
      ],
      [
        {
          show: !isCharge && !isIncomingAllocation && !isOutgoingAllocation,
          label: t('details.fields.purpose'),
          value: paymentStore.data.purpose || '-',
        },
        { show: true, label: t('details.fields.transactionReference'), value: paymentStore.data.description },
      ],
    ];
  }, [t, paymentStore.data]);
  const color = ColorUtils.getDepositPaymentStatusStyles(paymentStore.data?.status);
  return (
    <PageContainer title={t('title')} back backText={t(`backText${back ? '2' : ''}`)} to={back}>
      <View
        loading={paymentStore.loading}
        noData={!paymentStore.data}
        error={paymentStore.error}
        onRefresh={() => $paymentByIdStore.events.getPaymentById({ id: id! })}
      >
        <Spinner show={generateReceiptByIdStore.loading || generateOutgoingPaymentStore.loading || messageCentreStore.loading} position />
        {paymentStore.data && (
          <div className='flex flex-col gap-6'>
            <MessageTemplates />
            <section
              className={cn(
                'flex flex-col gap-6 bg-white p-6 dark:bg-soft-black',
                PaletteUtils.isVcfcPalette && 'rounded-xl dark:border dark:border-gray-black',
              )}
            >
              <div className='flex gap-5'>
                <div className='flex h-20 w-20 items-center justify-center rounded-full bg-primary'>
                  <TransactionLogoIcon />
                </div>
                <div className='flex flex-col gap-2'>
                  <div className='flex flex-wrap gap-3'>
                    <Text size='3xl'>
                      {AmountUtils.transformToAmountWithCurrency(paymentStore.data.beneficiaryAmount, paymentStore.data.currencyOut)}
                    </Text>
                    <Text className={cn('flex items-center rounded-sm p-1', color.bg, color.text)} color='custom'>
                      {t(`statuses.paymentTransaction.${paymentStore.data.status}`, { ns: 'common' })}
                    </Text>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    <Text size='sm'>{DateUtils.formatDate(paymentStore.data.valueDate)}</Text>
                    <Text size='sm' color='secondary'>
                      {FormatUtils.transactionTypeTransformation(t(`transferRequestType.${paymentStore.data.typeOfTransfer}`, { ns: 'common' }))}
                    </Text>
                  </div>
                </div>
              </div>
              {paymentStore.data.direction === DepositPaymentDirectionEnum.Outgoing &&
                paymentStore.data.typeOfTransfer !== DepositPaymentTypeOfTransferEnum.Charge && (
                  <Button className='h-12 py-3' fullWidth color='primary' onClick={downloadHandler}>
                    <Icon icon={<DocumentIcon />} color='inherit' />
                    {t('buttons.download', { ns: 'common' })}
                  </Button>
                )}
              <hr />
              <div className='flex flex-col gap-3'>
                <Text color='secondary'>{t('account.title')}</Text>
                <div className='flex items-center gap-3'>
                  <Icon
                    className='flex h-11 items-center rounded-sm border border-dark-white bg-soft-black px-5'
                    color='white'
                    icon={<GobblerIcon />}
                  />
                  <div className='flex flex-col justify-center'>
                    <Text>
                      {paymentStore.data.typeOfTransfer !== DepositPaymentTypeOfTransferEnum.Charge
                        ? paymentStore.data.accountNumber
                        : paymentStore.data.senderIban}
                    </Text>
                    {paymentStore.data.typeOfTransfer !== DepositPaymentTypeOfTransferEnum.Charge && (
                      <Text uppercase size='xs' color='gray'>
                        {paymentStore.data.accountType}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-3'>
                <Text color='secondary'>{t('details.title')}</Text>
                <div className={cn('flex flex-wrap gap-20', 'max-lg:flex-col max-lg:gap-2')}>
                  {content.map((col, idx) => (
                    <ul key={idx} className='flex flex-col gap-2'>
                      {col.map((item, idx) => {
                        if (!item.show) return null;
                        return (
                          <li key={idx}>
                            <Text color='secondary'>{item.label}</Text>
                            <Text className='flex items-center gap-2' weight='semibold' size='sm'>
                              {item.value}
                              {item.children}
                            </Text>
                          </li>
                        );
                      })}
                    </ul>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}
      </View>
    </PageContainer>
  );
};
